<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisErrorState from 'dashboard/components-next/LaisErrorState.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { formatCount, formatMetaCount } from '../utils/dataRegistryConsole';

const props = defineProps({
  title: { type: String, required: true },
  columns: { type: Array, required: true },
  rows: { type: Array, default: () => [] },
  meta: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  emptyTitle: { type: String, default: 'Nada encontrado' },
  emptyDescription: { type: String, default: '' },
  emptyIcon: { type: String, default: 'i-lucide-inbox' },
  loadingLabel: { type: String, default: 'Carregando...' },
  minWidth: { type: String, default: '960px' },
  testId: { type: String, default: 'data-registry-table' },
  rowKey: { type: String, default: 'id' },
  selectable: { type: Boolean, default: false },
});

const emit = defineEmits(['retry', 'page', 'select']);

const isEmpty = computed(
  () => !props.loading && !props.error && !props.rows.length
);
const timedOut = computed(() => Boolean(props.meta?.timedOut));
const currentPage = computed(() => props.meta?.currentPage || 1);
const totalLabel = computed(() => formatMetaCount(props.meta));
const rangeLabel = computed(() => {
  if (!props.rows.length) return '0';
  const start = (currentPage.value - 1) * (props.meta?.pageSize || 25) + 1;
  return `${formatCount(start)}–${formatCount(start + props.rows.length - 1)}`;
});
</script>

<template>
  <LaisCard :title="title" density="flush">
    <LaisErrorState
      v-if="error"
      class="m-4 min-h-[240px] shadow-none"
      :title="`Falha ao carregar ${title.toLowerCase()}`"
      :description="error"
      show-action
      :action-loading="loading"
      @action="emit('retry')"
    />

    <section
      v-else-if="loading"
      class="grid min-h-[220px] place-items-center p-5 text-center text-sm text-lais-text-body"
      role="status"
      aria-busy="true"
      :data-test="`${testId}-loading-state`"
    >
      <span>
        <span
          class="i-lucide-loader-circle mx-auto mb-3 block size-5 animate-spin"
          aria-hidden="true"
        />
        {{ loadingLabel }}
      </span>
    </section>

    <section
      v-else-if="meta?.requiresScope"
      class="m-4 grid min-h-[220px] place-items-center rounded-lais border border-dashed border-lais-primary bg-lais-primary-50 p-6 text-center"
      role="status"
      :data-test="`${testId}-scope-required-state`"
    >
      <div class="max-w-md">
        <span
          class="i-lucide-filter mx-auto block size-6 text-lais-primary"
          aria-hidden="true"
        />
        <h2 class="mt-3 font-redHatDisplay text-base font-semibold">
          Escolha um recorte para filtrar por conteúdo
        </h2>
        <p class="mt-1 text-sm text-lais-text-body">
          Registros tem centenas de milhões de linhas: o filtro por conteúdo
          precisa de uma fonte, tipo, risco, assunto ou busca para ser
          respondido. Na aba Pessoas o mesmo filtro roda sobre a base inteira.
        </p>
      </div>
    </section>

    <section
      v-else-if="timedOut"
      class="m-4 grid min-h-[220px] place-items-center rounded-lais border border-dashed border-amber-300 bg-amber-50 p-6 text-center"
      role="status"
      :data-test="`${testId}-timeout-state`"
    >
      <div class="max-w-md">
        <span
          class="i-lucide-timer-off mx-auto block size-6 text-amber-700"
          aria-hidden="true"
        />
        <h2 class="mt-3 font-redHatDisplay text-base font-semibold">
          Consulta excedeu o tempo limite
        </h2>
        <p class="mt-1 text-sm text-amber-800">
          O filtro atual varre uma faixa grande demais do registry. Escolha uma
          fonte, um tipo ou busque por CPF completo, ID ou início do nome.
        </p>
      </div>
    </section>

    <section
      v-else-if="isEmpty"
      class="m-4 grid min-h-[220px] place-items-center rounded-lais border border-dashed border-lais-border-soft bg-lais-bg-soft p-6 text-center"
      role="status"
      :data-test="`${testId}-empty-state`"
    >
      <div class="max-w-md">
        <span
          class="mx-auto grid size-12 place-items-center rounded-full bg-white text-lais-text-muted"
          aria-hidden="true"
        >
          <span class="size-5" :class="emptyIcon" />
        </span>
        <h2 class="mt-3 font-redHatDisplay text-base font-semibold">
          {{ emptyTitle }}
        </h2>
        <p class="mt-1 text-sm text-lais-text-muted">{{ emptyDescription }}</p>
      </div>
    </section>

    <!-- The horizontal scroll lives here, never on the page shell, so wide
         registry tables never push the layout out of the viewport. -->
    <div
      v-else
      class="max-w-full overflow-x-auto overscroll-x-contain"
      tabindex="0"
      role="region"
      :aria-label="title"
    >
      <table
        class="w-full text-sm"
        :style="{ minWidth }"
        :data-test="testId"
      >
        <thead
          class="sticky top-0 z-10 bg-lais-bg-soft text-left text-xs uppercase text-lais-text-muted"
        >
          <tr>
            <th
              v-for="column in columns"
              :key="column.id"
              class="whitespace-nowrap px-4 py-3 font-semibold"
              :class="column.class"
              scope="col"
            >
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-lais-border-soft">
          <tr
            v-for="row in rows"
            :key="row[rowKey]"
            class="align-top"
            :class="
              selectable
                ? 'cursor-pointer transition hover:bg-lais-bg-soft'
                : ''
            "
            @click="selectable && emit('select', row)"
          >
            <td
              v-for="column in columns"
              :key="`${row[rowKey]}-${column.id}`"
              class="px-4 py-3"
              :class="column.cellClass"
            >
              <slot :name="column.id" :row="row">
                {{ row[column.id] ?? '—' }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <footer
      class="flex flex-wrap items-center justify-between gap-3 border-t border-lais-border-soft px-4 py-3"
    >
      <div class="text-xs text-lais-text-muted">
        <span :data-test="`${testId}-range`">{{ rangeLabel }}</span>
        de
        <strong class="text-lais-text">{{ totalLabel }}</strong>
        <span v-if="meta?.countCapped && meta?.estimatedTableTotal">
          · ≈ {{ formatCount(meta.estimatedTableTotal) }} linhas na tabela
        </span>
      </div>
      <div class="flex gap-2">
        <LaisPrimaryButton
          icon="i-lucide-chevron-left"
          variant="ghost"
          size="sm"
          :disabled="currentPage <= 1 || loading"
          :data-test="`${testId}-prev`"
          @click="emit('page', -1)"
        >
          Anterior
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-chevron-right"
          variant="ghost"
          size="sm"
          :disabled="!meta?.hasNextPage || loading"
          :data-test="`${testId}-next`"
          @click="emit('page', 1)"
        >
          Próxima
        </LaisPrimaryButton>
      </div>
    </footer>
  </LaisCard>
</template>
